import Debug from 'debug'
import { EventStream } from '@/app/event-stream/event-stream';
import { TocItem } from '@/app/shared/models';
import {
  SectionInteractedEventName,
  SectionInteractionEvent
} from '@/app/statistics/events/section-interaction-events';
import { getPageFromHref } from '@/app/shared/utils/route-utils';
import { scrollCalculator } from './scroll-calculator';
import { restrictedThemes } from '@/app/shared/utils/restricted-themes'

const debug = Debug('content-utils')

export const queryElements = (selector: string, $parsedContent: any): any|null => {
  let elements: any[] = [];

  const foundElements: any = $parsedContent.find(selector);
  if (foundElements.length > 0) {
    elements = foundElements;
  }

  const filteredElements: any = $parsedContent.filter(selector);
  if (filteredElements.length > 0) {
    elements = [...elements, ...filteredElements];
  }

  if (elements.length === 0) {
    return null;
  }

  return elements;
};

export const getTextFragmentFromContent = (selector: string, $parsedContent: any): string => {
  return getFirstElement(selector, $parsedContent).text();
};

export const getHtmlFragmentFromContent = (selector: string, $parsedContent: any): string|null => {
  const $element = getFirstElement(selector, $parsedContent);
  if ($element === null) {
    return null;
  }

  return $element.html();
};

export const getFirstElement = (selector: string, $parsedContent: any): any => {
  const elements: any = queryElements(selector, $parsedContent);
  if (elements === null) {
    return null;
  }

  return $(elements[0]);
};

export const removeElement = (selector: string): void => {
  $(() => {
    $(selector).remove();
  });
};

export const appendElement = (selector: string, element: any): void => {
  $(() => {
    $(selector).append(element);
  });
};

export const replaceInnerElement = (selector: string, element: any): void => {
  $(() => {
    $(selector).html(element);
  });
};

export const replaceWithElement = (selector: string, element: any): void => {
  $(() => {
    $(selector).replaceWith(element);
  });
};

// export const replaceBasePathInContent = (content: string, aspectBasePath: string = ''): string => {
//   const courseBasePath: string = process.env.VUE_APP_COURSE_BASE_PATH;
//   let path: string = courseBasePath;
//   if (aspectBasePath !== '') {
//     path = `${path}/${aspectBasePath}`;
//   }
//   return content
//     .replace(/\$\{basePath\}\//g, path + '/')
//     .replace(/\$\{basePath\}/g, path + '/');
// };

export const listenScroll = (page: string, sectionId: string, eventStream: EventStream): void => {
  const $scrollElement: JQuery<HTMLDivElement> = $('.scrollable-box');
  const $mainContent: JQuery<HTMLDivElement> = $('.main-content');

  setTimeout(() => {
    // HACK(szazo): timeout because initial height calculation does not return the good percent
    // maybe it is caused by other timing issue
    const initialScrollPercent = calculateScrollPercent($mainContent, $scrollElement)
    debug(`initial scroll percent (percent=${initialScrollPercent}`)
    eventStream.dispatch(createScrolledEvent(initialScrollPercent, sectionId));
  }, 100)

  $scrollElement.on('scroll', () => {
    const scrollPercent = calculateScrollPercent($mainContent, $scrollElement)
    debug(`dispatching scroll (scroll=${scrollPercent})`)
    eventStream.dispatch(createScrolledEvent(scrollPercent, sectionId));
  });
};

function calculateScrollPercent($content: JQuery<HTMLDivElement>, $scrollBox: JQuery<HTMLDivElement>) {
  const contentHeight: number = $content.outerHeight(true) || 0 // with margin
  const marginBottom: number = parseInt($content.css('margin-bottom'), 10);

  const contentHeightWithoutBottomMargin = contentHeight >= marginBottom ? contentHeight - marginBottom : 0

  const visibleHeight: number = $scrollBox.height() || 0;
  const scrollTop = $scrollBox.scrollTop() || 0
  const calculator = scrollCalculator({ visibleHeight: visibleHeight, contentHeight: contentHeightWithoutBottomMargin })

  const percent = Math.ceil(calculator(scrollTop) * 100)
  debug(`scroll calculation (percent=${percent}, contentHeight=${contentHeight}, contentHeightWithoutMargin=${contentHeightWithoutBottomMargin}, visibleHeight=${visibleHeight}, scrollElement.scrollTop=${scrollTop})`)

  return percent > 100 ? 100 : percent
}

const createScrolledEvent = (scrolled: number, sectionId: string): SectionInteractionEvent => {
  return {
    name: SectionInteractedEventName,
    payload: {
      scrolledPercent: scrolled,
      sectionId
    }
  };
};

export const maximumPossiblePageHeight = (): number => {
  const pageSelector: string = '.course-content.page';
  let height: number = $(pageSelector).height() as number;
  if (typeof height === 'undefined') {
    height = 0;
  }

  const $pageComponents: any = $('.page.component');
  const pageComponentsCount: number = $pageComponents.length;
  const $nextButton: any = $('.next-page');
  if (pageComponentsCount > 0 && $nextButton.length > 0) {
    const buttonHeight: number = $nextButton.outerHeight();
    const buttonMarginTop: number = parseInt($nextButton.css('margin-top'), 10);
    height = height - buttonHeight - buttonMarginTop;

    $pageComponents.each((index: number, element: any) => {
      height += $(element).height() as number;
    });
  }

  return height;
};

export const calculateSectionsCount = (tocItems: TocItem[]): number => {
  let count: number = 0;
  for (const tocItem of tocItems) {
    if (tocItem.href && tocItem.type !== 'intro') {
      count++;
    }
  }

  return count;
};

export const getOffsetTop = (selector: string): number => {
  return ($(selector).offset() as any).top;
};

export const getPositionTop = (selector: string): number => {
  const position: any = $(selector).position();
  if (!position) {
    return 0;
  }

  return ($(selector).position() as any).top;
};

export const defaultCourseImageUri = () => {
  if (restrictedThemes(['minerva'])) {
    return require('@/assets/course/default_minerva.jpg');
  }
  if (restrictedThemes(['mavir', 'mavir-orange'])) {
    return require('@/assets/course/default_mavir.jpg');
  }
  return require('@/assets/course/default_skillgo.jpg');
};

export const scrollToTop = () => {
  $('.scrollable-box').scrollTop(0);
};

// export const walkObjectAndReplaceBasePaths = (obj: any, aspectBasePath: string = ''): any => {
//   if (Array.isArray(obj)) {
//     let arrayResult: any = [];
//     for (let i = 0; i < obj.length; i++) {
//       const key = i;
//       const value = obj[i];
//       arrayResult = handleKeyValueInObject(key, value, arrayResult, aspectBasePath);
//     }

//     return arrayResult;
//   }

//   let objectResult: any = {};
//   Object.entries(obj).forEach(([key, value]) => {
//     objectResult = handleKeyValueInObject(key, value, objectResult, aspectBasePath);
//   });

//   return objectResult;
// };

// const handleKeyValueInObject = (key: string | number, value: any, result: any, aspectBasePath: string = ''): any => {
//   if (value && typeof value === 'object') {
//     result[key] = walkObjectAndReplaceBasePaths(value, aspectBasePath);
//   } else if (typeof value === 'string') {

//     if (value.includes('basePath')) {
//       console.error('BASEPATH in content', value)
//     }

//     result[key] = replaceBasePathInContent(value, aspectBasePath);
//   } else {
//     result[key] = value;
//   }

//   return result;
// };

export const sectionVisible = (sectionId: string): boolean => {
  const $section = $(`#${sectionId}`);
  return $section.is(':visible');
};

export const sectionsOnSamePage = (section1: TocItem, section2: TocItem): boolean => {
  if (!section1.href || !section2.href) {
    return false;
  }

  const page1: string = getPageFromHref(section1.href);
  const page2: string = getPageFromHref(section2.href);
  return page1 === page2;
};

export const removeHtmlTags = (html: string): string => {
  const $item = $('<div/>').html(html);
  return $item.text();
};
